// src/views/Tarjetas/ListaDeLotesTarjeta/VerLote.jsx
import React, { useEffect, useMemo, useState } from "react";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import { useParams } from "react-router-dom";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchBlob, apiFetchJson } from "../../../utils/http";
import { shouldRunOnce } from "../../../utils/devOnce";

export default function VerLotes({ onVolver, loteId: loteIdProp, lote }) {
  // 1) Tomar el id desde la URL si existe; si no, desde props (loteId | lote.id*)
  const { loteId: loteIdParam } = useParams();
  const loteIdResolved =
    loteIdParam ??
    loteIdProp ??
    lote?.id ??
    lote?.idLote ??
    lote?.loteId ??
    null;

  const loteId = Number(loteIdResolved);

  // datos
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [lastUrl, setLastUrl] = useState("");
  const [totalBackend, setTotalBackend] = useState(0);

  // header
  const [fechaLote, setFechaLote] = useState("");

  // paginación (UI 1-based)
  const [paginaActual, setPaginaActual] = useState(1);
  const [porPagina, setPorPagina] = useState(30);

  // descarga
  const [descargando, setDescargando] = useState(false);

  // helpers de payload
  const extractArray = (json) => {
    if (!json || typeof json !== "object") return [];
    if (Array.isArray(json.items)) return json.items;
    if (Array.isArray(json.content)) return json.content;
    if (Array.isArray(json.list)) return json.list;
    return [];
  };
  const extractTotal = (json, itemsLen) => {
    if (!json || typeof json !== "object") return itemsLen;
    if (typeof json.totalElements === "number") return json.totalElements;
    if (typeof json.total === "number") return json.total;
    if (json.pagination && typeof json.pagination.total === "number")
      return json.pagination.total;
    return itemsLen;
  };

  const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString("es-ES"));

  const vehiculoText = (row) => {
    const parts = [];
    if (row.chapa) parts.push(row.chapa);
    if (row.vehiculoMarca) parts.push(row.vehiculoMarca);
    if (row.vehiculoModelo) parts.push(row.vehiculoModelo);
    return parts.length ? parts.join(" ") : "—";
  };

  const fetchTarjetas = async (page = paginaActual, size = porPagina) => {
    if (!loteId || Number.isNaN(loteId)) {
      setErrorMsg("Id de lote inválido.");
      setCargando(false);
      setItems([]);
      setTotalBackend(0);
      return;
    }

    setCargando(true);
    setErrorMsg("");

    try {
      const url = apiRoutes.lotes.tarjetasDeLote(loteId, page - 1, size);
      setLastUrl(url);
      const json = await apiFetchJson(url);

      const arr = extractArray(json);
      const total = extractTotal(json, arr.length);

      const metaFecha =
        json?.meta?.fechaLote || json?.fechaLote || json?.loteFecha || "";
      if (metaFecha) setFechaLote(metaFecha);

      setItems(arr);
      setTotalBackend(typeof total === "number" ? total : arr.length);
    } catch (e) {
      const status = e?.status
        ? ` [${e.status}${e.statusText ? " " + e.statusText : ""}]`
        : "";
      setErrorMsg(`No se pudo cargar el detalle del lote${status}.`);
      setItems([]);
      setTotalBackend(0);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (!shouldRunOnce(`VerLote:init:${loteIdResolved ?? "none"}`)) return;
    fetchTarjetas(1, porPagina);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loteIdResolved]);

  // paginación
  const totalPaginas = Math.max(1, Math.ceil(totalBackend / porPagina));
  const startIndex = (paginaActual - 1) * porPagina;
  const endIndex = Math.min(startIndex + porPagina, totalBackend);
  const indiceInicio = totalBackend === 0 ? 0 : startIndex + 1;
  const indiceFin = endIndex;

  const cambiarPagina = (n) => {
    const next = Math.min(Math.max(1, n), totalPaginas);
    setPaginaActual(next);
    fetchTarjetas(next, porPagina);
  };

  const manejarCambioCantidad = (e) => {
    const nueva = Number(e.target.value);
    setPorPagina(nueva);
    setPaginaActual(1);
    fetchTarjetas(1, nueva);
  };

  // descarga TXT
  const descargarArchivo = async () => {
    if (!loteId || Number.isNaN(loteId)) {
      setErrorMsg("Id de lote inválido.");
      return;
    }
    try {
      setDescargando(true);
      const url = apiRoutes.lotes.generarArchivo(loteId);
      const { blob, filename } = await appFetchBlob(
  url,
  {
    accept: "text/plain",
  },
  {
    showLoading: true,   // 👈 esto activa el overlay "Procesando..."
  }
);

      const name =
        filename || `TARBANCO_${String(loteId).padStart(5, "0")}.txt`;
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 2000);
    } catch (e) {
      setErrorMsg(`No se pudo generar el archivo. ${e?.message ?? ""}`);
    } finally {
      setDescargando(false);
    }
  };

  const fechaHeader = useMemo(() => {
    if (!fechaLote) return "";
    return String(fechaLote).replace("T", " ");
  }, [fechaLote]);

  return (
    <div className="p-0 m-0">
      {/* Barra superior (Volver + selector + descargar a la derecha) */}
      <div className="mb-2 flex items-center gap-2">
        <button className="btn-fil" onClick={onVolver}>
          <FaArrowLeft className="inline-block mr-2" /> Volver
        </button>

        <div className="ml-2 flex items-center gap-2">
          <label htmlFor="pp" className="label-strong">
            Mostrar por página:
          </label>
          <select
            id="pp"
            value={porPagina}
            onChange={manejarCambioCantidad}
            className="select"
          >
            <option value={30}>30</option>
            <option value={40}>40</option>
            <option value={50}>50</option>
          </select>
        </div>

        <div className="ml-auto">
          <button
            className={`btn-fil ${descargando ? "opacity-50 pointer-events-none" : ""}`}
            onClick={descargarArchivo}
            disabled={descargando}
            title={descargando ? "Generando…" : "Generar Archivo de Embozado"}
          >
            <FaDownload className="inline -mt-1 mr-1" />
            Generar Archivo de Embozado
          </button>
        </div>
      </div>

      {/* errores */}
      {errorMsg && (
        <div className="mt-2 mb-3 p-3 rounded bg-red-50 text-red-700 text-sm">
          <div className="font-semibold">Error</div>
          <div>{errorMsg}</div>
          {lastUrl ? (
            <div className="mt-1 opacity-80 break-all">
              Consulta: <code>{lastUrl}</code>
            </div>
          ) : null}
        </div>
      )}

      {/* tabla */}
      {cargando ? (
        <div className="loading-text">Cargando tarjetas del lote…</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="table-card">
              <thead className="table-head">
                <tr>
                  <th colSpan={9} className="cell text-center">
                    Detalle del Lote #{Number.isNaN(loteId) ? "—" : loteId}
                    {fechaHeader ? (
                      <span className="ml-2 text-xs font-normal opacity-80">
                        (fecha: {fechaHeader})
                      </span>
                    ) : null}
                  </th>
                </tr>
                <tr>
                  <th className="cell text-left">Nro. de Tarjeta</th>
                  <th className="cell text-left">Saldo</th>
                  <th className="cell text-left">Límite</th>
                  <th className="cell text-left">Ctro. Costo</th>
                  <th className="cell text-left">Tipo</th>
                  <th className="cell text-left">Cliente</th>
                  <th className="cell text-left">EE.SS.</th>
                  <th className="cell text-left">Estado</th>
                  <th className="cell text-left">Vehículo</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td className="cell text-center" colSpan={9}>
                      Ningún ítem encontrado.
                    </td>
                  </tr>
                ) : (
                  items.map((row, idx) => (
                    <tr
                      key={row.id ?? idx}
                      className={idx % 2 === 0 ? "row-alt" : "bg-white"}
                    >
                      <td className="cell">{row.numero ?? "—"}</td>
                      <td className="cell">{fmtNum(row.valorSaldo)}</td>
                      <td className="cell">{fmtNum(row.valorLimite)}</td>
                      <td className="cell">{row.dataCentroCosto ?? "—"}</td>
                      <td className="cell">{row.codigoTipo ?? "—"}</td>
                      <td className="cell">{row.clienteNombre ?? "—"}</td>
                      <td className="cell">{row.eessNombre ?? "DEFAULT"}</td>
                      <td className="cell">{row.estadoNombre ?? "—"}</td>
                      <td className="cell">{vehiculoText(row)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            {`${totalBackend.toLocaleString("es-ES")} item${
              totalBackend !== 1 ? "s" : ""
            } encontrados, mostrando de ${indiceInicio} a ${indiceFin}.`}
          </div>

          {/* paginación */}
          <nav className="pager">
            <button
              onClick={() => cambiarPagina(1)}
              disabled={paginaActual === 1}
              className={paginaActual === 1 ? "pager-off" : "pager-btn"}
            >
              Primera
            </button>
            <button
              onClick={() => cambiarPagina(paginaActual - 1)}
              disabled={paginaActual === 1}
              className={paginaActual === 1 ? "pager-off" : "pager-btn"}
            >
              Anterior
            </button>
            {[...Array(Math.max(1, Math.ceil(totalBackend / porPagina)))].map(
              (_, i) => {
                const num = i + 1;
                if (
                  num === 1 ||
                  num === Math.ceil(totalBackend / porPagina) ||
                  (num >= paginaActual - 2 && num <= paginaActual + 2)
                ) {
                  return (
                    <button
                      key={num}
                      onClick={() => cambiarPagina(num)}
                      className={
                        paginaActual === num ? "pager-active" : "pager-btn"
                      }
                    >
                      {num}
                    </button>
                  );
                }
                if (num === paginaActual - 3 || num === paginaActual + 3) {
                  return (
                    <span key={num} className="px-2 py-1">
                      …
                    </span>
                  );
                }
                return null;
              },
            )}
            <button
              onClick={() => cambiarPagina(paginaActual + 1)}
              disabled={
                paginaActual ===
                Math.max(1, Math.ceil(totalBackend / porPagina))
              }
              className={
                paginaActual ===
                Math.max(1, Math.ceil(totalBackend / porPagina))
                  ? "pager-off"
                  : "pager-btn"
              }
            >
              Próxima
            </button>
            <button
              onClick={() =>
                cambiarPagina(Math.max(1, Math.ceil(totalBackend / porPagina)))
              }
              disabled={
                paginaActual ===
                Math.max(1, Math.ceil(totalBackend / porPagina))
              }
              className={
                paginaActual ===
                Math.max(1, Math.ceil(totalBackend / porPagina))
                  ? "pager-off"
                  : "pager-btn"
              }
            >
              Última
            </button>
          </nav>
        </>
      )}
    </div>
  );
}
